import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Check, X, Settings, Users, Search, Eye, Edit, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';
import InfoBox from '../components/InfoBox';

// ── Permission sections with labels ──────────────────────────
const SECTIONS = [
  { key: 'inventory',   label: 'Inventory Management',        hasEdit: true },
  { key: 'bags',        label: 'Transport Bags & Operations', hasEdit: true },
  { key: 'participants',label: 'Participants & Prospects',    hasEdit: true },
  { key: 'trips',       label: 'Trip Management',            hasEdit: true },
  { key: 'licenses',    label: 'License & Credential Review',hasEdit: true },
  { key: 'hotels',      label: 'Hotel & Room Management',    hasEdit: true },
  { key: 'reminders',   label: 'Reminders & Communication',  hasEdit: true },
  { key: 'financials',  label: 'Financial Data (Costs)',      hasEdit: false },
];

const defaultPerms = () => Object.fromEntries(SECTIONS.map(s => [s.key, { view: false, edit: false }]));

// ── Responsibility Types Manager ──────────────────────────────
const RespTypesManager = ({ onClose }) => {
  const [types, setTypes] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/responsibility-types').then(r => { setTypes(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await api.post('/responsibility-types', { name });
      setTypes(prev => [...prev, res.data]);
      setNewName('');
      toast.success('Responsibility type added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/responsibility-types/${id}`);
      setTypes(prev => prev.filter(t => t.resp_id !== id));
      toast.success('Removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handleToggleActive = async (rt) => {
    try {
      await api.put(`/responsibility-types/${rt.resp_id}`, { active: !rt.active });
      setTypes(prev => prev.map(t => t.resp_id === rt.resp_id ? { ...t, active: !t.active } : t));
    } catch { toast.error('Failed to update'); }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Manage Responsibility Types</DialogTitle></DialogHeader>
      <div className="py-2 space-y-3">
        <div className="flex gap-2">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New responsibility name..."
            onKeyDown={e => e.key === 'Enter' && handleAdd()} className="flex-1" data-testid="new-resp-input" />
          <Button onClick={handleAdd} className="bg-primary" data-testid="add-resp-btn">Add</Button>
        </div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {loading ? <p className="text-center text-slate-400 py-4">Loading...</p> :
            types.map(rt => (
              <div key={rt.resp_id} className={`flex items-center justify-between p-2.5 rounded-xl border ${rt.active !== false ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'}`}>
                <span className={`text-sm ${rt.active !== false ? 'text-slate-700' : 'text-slate-400 line-through'}`}>{rt.name}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleToggleActive(rt)}
                    className={`text-xs px-2 py-0.5 rounded-full border ${rt.active !== false ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-slate-200 text-slate-400 hover:bg-slate-100'}`}>
                    {rt.active !== false ? 'Active' : 'Hidden'}
                  </button>
                  <button onClick={() => handleDelete(rt.resp_id)} className="p-1 text-slate-300 hover:text-red-500" data-testid={`delete-resp-${rt.resp_id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          }
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Done</Button></DialogFooter>
    </DialogContent>
  );
};

// ── Invite modal ──────────────────────────────────────────────
const InviteModal = ({ onClose, onDone }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('staff');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email) { toast.error('Email is required'); return; }
    setSending(true);
    try {
      await api.post('/invites', { email, name, user_type: userType });
      toast.success(`Invitation sent to ${email}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
    finally { setSending(false); }
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader><DialogTitle>Invite New User</DialogTitle></DialogHeader>
      <div className="py-2 space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Email Address *</label>
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com"
            type="email" data-testid="invite-email-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Name (optional)</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" data-testid="invite-name-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Account Type</label>
          <Select value={userType} onValueChange={setUserType}>
            <SelectTrigger data-testid="invite-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="participant">Participant (trip member)</SelectItem>
              <SelectItem value="staff">Staff (admin/support, not a trip participant)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1">
            {userType === 'staff' ? 'Staff accounts appear here but not on the Participants roster.' : 'Participant accounts appear on the Participants roster and can be assigned to trips.'}
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="bg-primary" onClick={handleSend} disabled={sending} data-testid="send-invite-confirm-btn">
          {sending ? 'Sending...' : 'Send Invitation'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════
const PermissionsPage = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({}); // userId → perms object
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const [showRespManager, setShowRespManager] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
      // Fetch permissions for all non-admin users
      const permsMap = {};
      await Promise.all(res.data.map(async u => {
        try {
          const p = await api.get(`/users/${u.user_id}/permissions`);
          permsMap[u.user_id] = p.data;
        } catch { permsMap[u.user_id] = defaultPerms(); }
      }));
      setPermissions(permsMap);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePermChange = async (userId, section, level, value) => {
    const userIsAdmin = users.find(u => u.user_id === userId)?.role === 'admin';
    if (userIsAdmin) return; // admins always have all permissions

    setSaving(s => ({ ...s, [`${userId}-${section}-${level}`]: true }));
    const current = permissions[userId] || defaultPerms();
    const newPerms = { ...current, [section]: { ...current[section], [level]: value } };
    // If removing view, also remove edit
    if (level === 'view' && !value) newPerms[section].edit = false;
    // If adding edit, also add view
    if (level === 'edit' && value) newPerms[section].view = true;

    setPermissions(prev => ({ ...prev, [userId]: newPerms }));
    try {
      await api.put(`/users/${userId}/permissions`, newPerms);
    } catch { toast.error('Failed to save permission'); }
    finally { setSaving(s => ({ ...s, [`${userId}-${section}-${level}`]: false })); }
  };

  const handleUserTypeChange = async (userId, userType) => {
    try {
      await api.put(`/users/${userId}/user-type`, { user_type: userType });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, user_type: userType } : u));
      toast.success('Account type updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await api.put(`/users/${userId}/role`, null, { params: { role } });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role } : u));
      toast.success('Role updated');
    } catch { toast.error('Failed to update role'); }
  };

  const filtered = users.filter(u => {
    // Only show Full Admins and users with elevated_access — hide standard users
    const isFullAdmin = u.role === 'admin';
    const isElevated = u.elevated_access === true;
    if (!isFullAdmin && !isElevated) return false;

    if (filterType === 'staff' && u.user_type !== 'staff') return false;
    if (filterType === 'participant' && (u.user_type === 'staff')) return false;
    const term = search.toLowerCase();
    return !term || (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term);
  });

  const Checkbox = ({ checked, onChange, disabled, testId }) => (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testId}
      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0
        ${disabled ? 'opacity-40 cursor-not-allowed border-slate-200 bg-slate-50' :
          checked ? 'bg-primary border-primary' : 'border-slate-300 hover:border-primary/50 bg-white'}`}
    >
      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    </button>
  );

  const permActions = (
    <>
      <Button variant="outline" size="sm" onClick={() => setShowRespManager(true)} className="text-xs h-8" data-testid="manage-resp-btn">
        <Settings className="w-3.5 h-3.5 mr-1.5" />Responsibility Types
      </Button>
    </>
  );

  return (
    <PageLayout title="Permissions & Access" headerActions={permActions}>
          {/* Info card */}
          <InfoBox className="mb-5">
            <p className="font-medium mb-0.5">Elevated Access Users Only</p>
            <p>Only <strong>Full Admins</strong> and users granted elevated access from the Participants page appear here. Standard participants are managed on the Participants page. Use the "Grant permissions access" checkbox in a participant's detail panel to add them here.</p>
          </InfoBox>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 w-52" data-testid="permissions-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-9 w-44" data-testid="filter-user-type">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({filtered.length})</SelectItem>
                <SelectItem value="participant">Participants</SelectItem>
                <SelectItem value="staff">Staff Only</SelectItem>
              </SelectContent>
            </Select>
            <span className="ml-auto text-sm text-slate-400">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Permissions grid */}
          {loading ? (
            <div className="py-16 text-center text-slate-400">Loading users...</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400">No users found</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(u => {
                const isAdminUser    = u.role === 'admin';
                const isPartialAdmin = u.role === 'partial_admin';
                const perms = permissions[u.user_id] || defaultPerms();
                const isExpanded = expandedUser === u.user_id;

                return (
                  <Card key={u.user_id} className={`${isAdminUser ? 'border-primary/20 bg-primary/2' : isPartialAdmin ? 'border-amber-200 bg-amber-50/30' : ''}`}
                    data-testid={`user-perm-row-${u.user_id}`}>
                    <CardContent className="p-0">
                      {/* User header row */}
                      <div
                        className="p-4 cursor-pointer hover:bg-slate-50 rounded-t-xl transition-colors"
                        onClick={() => setExpandedUser(isExpanded ? null : u.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            {u.picture
                              ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                              : <Users className="w-4 h-4 text-primary" />}
                          </div>
                          {/* Name + email */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-800 text-sm">{u.name || '—'}</span>
                              {u.user_type === 'staff' && (
                                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Staff</Badge>
                              )}
                              {isAdminUser && (
                                <Badge className="bg-primary/10 text-primary border-0 text-xs">Full Admin</Badge>
                              )}
                              {isPartialAdmin && (
                                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Partial Admin</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{u.email}</p>
                          </div>
                          {/* Expand toggle */}
                          <button className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                        {/* Controls — wrap on mobile */}
                        <div className="flex flex-wrap items-center gap-2 mt-2 pl-12" onClick={e => e.stopPropagation()}>
                          <Select value={u.role || 'participant'} onValueChange={v => handleRoleChange(u.user_id, v)}>
                            <SelectTrigger className="h-7 text-xs w-28 sm:w-36" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="participant" className="text-xs">Standard</SelectItem>
                              <SelectItem value="partial_admin" className="text-xs">Partial Admin</SelectItem>
                              <SelectItem value="admin" className="text-xs">Full Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={u.user_type || 'participant'} onValueChange={v => handleUserTypeChange(u.user_id, v)}>
                            <SelectTrigger className="h-7 text-xs w-28 sm:w-32" data-testid={`type-${u.user_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="participant" className="text-xs">Trip Participant</SelectItem>
                              <SelectItem value="staff" className="text-xs">Support Staff</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await api.post('/auth/forgot-password', { email: u.email });
                                toast.success(`Password reset sent to ${u.email}`);
                              } catch { toast.error('Failed to send'); }
                            }}
                            className="text-xs text-slate-400 hover:text-primary transition-colors whitespace-nowrap"
                            title="Send password reset email"
                            data-testid={`reset-pwd-${u.user_id}`}
                          >
                            Reset pwd
                          </button>
                        </div>
                      </div>

                      {/* Permissions grid (expanded) */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                          {isAdminUser ? (
                            <p className="text-sm text-primary/70 italic py-2">
                              Full Admin — has access to all sections. No configuration needed.
                            </p>
                          ) : isPartialAdmin ? (
                            <div className="overflow-x-auto">
                              <p className="text-xs text-amber-700 mb-3">
                                Configure which sections this Partial Admin can view or edit:
                              </p>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr>
                                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 pr-4 w-56">Section</th>
                                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 w-20">
                                      <div className="flex items-center justify-center gap-1"><Eye className="w-3 h-3" />View</div>
                                    </th>
                                    <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wide pb-2 w-24">
                                      <div className="flex items-center justify-center gap-1"><Edit className="w-3 h-3" />Edit/Manage</div>
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {SECTIONS.map(s => (
                                    <tr key={s.key} className="border-t border-slate-50">
                                      <td className="py-2 pr-4 text-slate-700">{s.label}</td>
                                      <td className="py-2 text-center">
                                        <div className="flex justify-center">
                                          <Checkbox
                                            checked={perms[s.key]?.view || false}
                                            onChange={v => handlePermChange(u.user_id, s.key, 'view', v)}
                                            testId={`perm-${u.user_id}-${s.key}-view`}
                                          />
                                        </div>
                                      </td>
                                      <td className="py-2 text-center">
                                        <div className="flex justify-center">
                                          {s.hasEdit ? (
                                            <Checkbox
                                              checked={perms[s.key]?.edit || false}
                                              onChange={v => handlePermChange(u.user_id, s.key, 'edit', v)}
                                              testId={`perm-${u.user_id}-${s.key}-edit`}
                                            />
                                          ) : (
                                            <span className="text-xs text-slate-300">—</span>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 italic py-2">
                              Standard — no elevated permissions. Change to Partial Admin to configure access.
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

      {/* Responsibility types manager */}
      <Dialog open={showRespManager} onOpenChange={setShowRespManager}>
        <RespTypesManager onClose={() => setShowRespManager(false)} />
      </Dialog>
    </PageLayout>
  );
};

export default PermissionsPage;
